import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { NotAuthorized } from "@/components/NotAuthorized";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { toast } from "@/components/podkit/toasts/use-toast";
import { Heading3, Subheading } from "@/components/podkit/typography/Headings";
import { Switch, type SwitchState } from "@/components/Switch";
import { useDocumentTitle } from "@/hooks/use-document-title";
import {
    KNOWN_EXPERIMENTS,
    useExperimentToggle,
    useExperimentVisibleInSettings,
    useAnyExperimentVisibleInSettings,
    ExperimentNames,
    type ExperimentName,
} from "@/hooks/use-experiment";
import { useSegmentTrack } from "@/hooks/use-segment";
import { IconTestTube } from "central-icons/IconTestTube";
import type { FC } from "react";

type ExperimentsSettingsPageProps = {
    hideDescription?: boolean;
};

export const ExperimentsSettingsPage: FC<ExperimentsSettingsPageProps> = ({ hideDescription }) => {
    useDocumentTitle("Experiments");

    const { visible: anyVisible, loading: visibilityLoading } = useAnyExperimentVisibleInSettings();

    if (visibilityLoading) {
        return <LoadingState />;
    }

    // ConfigCat targeting rules determine visibility per org/tier/user.
    // If no experiments are visible for this user, they shouldn't see this page.
    if (!anyVisible) {
        return <NotAuthorized />;
    }

    return (
        <div className="flex flex-col gap-8" data-testid="experiments-settings-page">
            {!hideDescription && <ExperimentsHeader />}

            <div className="flex flex-col gap-4">
                {Object.values(ExperimentNames).map((name) => (
                    <ExperimentCard key={name} experimentName={name} />
                ))}
            </div>
        </div>
    );
};

const ExperimentsHeader: FC = () => (
    <div className="space-y-1">
        <div className="flex items-center gap-2">
            <IconTestTube size={20} />
            <Heading3>Experiments</Heading3>
        </div>
        <Subheading>
            Enable experimental features to try out new functionality before it&apos;s generally available. These
            features may change or be removed at any time.
        </Subheading>
    </div>
);

type ExperimentCardProps = {
    experimentName: ExperimentName;
};

const ExperimentCard: FC<ExperimentCardProps> = ({ experimentName }) => {
    const experiment = KNOWN_EXPERIMENTS[experimentName];
    const { visible } = useExperimentVisibleInSettings(experimentName);
    const { enabled, setEnabled } = useExperimentToggle(experimentName);
    const segmentTrack = useSegmentTrack();

    // Don't render experiments that aren't visible for this user's targeting
    if (!visible) {
        return null;
    }

    const handleToggle = (checked: boolean) => {
        setEnabled(checked);

        const event = checked ? "experiment_enabled" : "experiment_disabled";
        segmentTrack(event, {
            experiment_name: experimentName,
            experiment_display_name: experiment.name,
            source: "settings",
        });

        if (checked) {
            if (experimentName === "ralph") {
                toast({
                    title: (
                        <span className="flex items-center gap-2">
                            <IconRalphWiggum size={24} />
                            <span>Ralph Mode enabled!</span>
                        </span>
                    ),
                });
            } else {
                toast({ title: `${experiment.name} enabled!` });
            }
        } else {
            toast({ title: `${experiment.name} disabled` });
        }
    };

    const displayState: SwitchState = enabled ? "checked" : "unchecked";

    return (
        <div className="flex items-center justify-between rounded-xl border border-border-base bg-surface-secondary p-4">
            <div className="flex items-center gap-3">
                {experimentName === "ralph" ? (
                    <IconRalphWiggum size={32} className="shrink-0" />
                ) : (
                    <IconTestTube size={32} className="shrink-0" />
                )}
                <div>
                    <div className="font-medium text-content-primary">{experiment.name}</div>
                    <div className="text-sm text-content-secondary">{experiment.description}</div>
                </div>
            </div>
            <Switch
                state={displayState}
                onToggle={handleToggle}
                aria-label={`Toggle ${experiment.name}`}
                data-tracking-id={`toggle-experiment-${experimentName}`}
            />
        </div>
    );
};
